import { observer } from "mobx-react"; // Import observer from mobx-react for state management
import moment from "moment"; // Import moment for date and time manipulation
import React from "react"; // Import React
import { FormattedMessage } from "react-intl"; // Import FormattedMessage for i18n
import demoStoreIcon from "../../../../assets/Images/man.png"; // Import demo store icon
import allStaffIcon from "../../../../assets/Images/teamwork.png"; // Import all staff icon
import {
  formatTimeString, // Import formatTimeString utility
  getOverlappingBreaksByWeeklyTime, // Import getOverlappingBreaksByWeeklyTime utility
  getOverlappingEventsByWeeklyTime, // Import getOverlappingEventsByWeeklyTime utility
  isCurrentDateTimeGreater, // Import isCurrentDateTimeGreater utility
  isDayClosedWeekly, // Import isDayClosedWeekly utility
  isTimeOuterThanShiftColourModeUsingMinMaxTimeEffortWeekly,
  isTimeWithinShift, // Import isTimeOuterThanShiftColourModeUsingMinMaxTimeEffortWeekly utility
} from "../../../../constants/constants"; // Import utility functions
import bookingStatusEnum from "../../../../constants/enums/booking-status-enum"; // Import booking status enum
import { formatMessage } from "../../../../translations/format-message"; // Import formatMessage for translations
import BreakEventComponent from "./break-event-component"; // Import BreakEventComponent
import CompletedEventComponent from "./completed-event-component"; // Import CompletedEventComponent
import EmptyCell from "./empty-cell-component"; // Import EmptyCell component
import Hours from "./hour-component"; // Import Hours component
import InProgressEvent from "./inprogress-event-component"; // Import InProgressEvent component
import Minutes from "./minutes-component"; // Import Minutes component
import ReceptionEventComponent from "./reception-event-component"; // Import ReceptionEventComponent
import { useStore } from "../../../../contexts/store-provider";

// WeeklySchedulerComponent renders the weekly schedule view with staff and time slots
interface IProps {
  minutes: any[]; // Array of minute intervals
  hoursShown: any; // Hours to be displayed
  hours: any[]; // Hour data
  minuteInterval: number; // Interval between minutes
  DEFAULT_TOP_MARGIN: number; // Default top margin for layout
  TOTAL_ALLOWED_EVENTS_IN_ROW: number; // Max events in a row
  CELL_WIDTH: number; // Width of each cell
  currentDate?: string; // Currently selected date
  view?: string; // View type (e.g., daily)
  getAllStaffOptions?: any[]; // Options for all staff
  handleStaffSelect: (staffId: number, staffName: string) => void; // Function to handle staff selection
  selectedStaff?: string | number; // Selected staff ID
  openTime?: string; // Opening time
  minutesTd: string[]; // Array of minute intervals for table
  getStaffAppointmentsNonStaff: any[]; // Staff appointments data
  getBreaks: any[]; // Breaks data
  openReservationWindow: (params: any) => void; // Function to open reservation window
  droppedEvents?: any[]; // Dropped events data
  days: any; // Days data
  hourColumnWidthPx: any; // Hour column width in pixels
  minuteColumnWidthPx: any; // Minute column width in pixels
  tableRef: any; // Reference to the table element
  getReservationSettingDetail: any; // Reservation settings detail
  storeId: number; // Pass the store ID
  viewOnly: boolean; // Pass the view only flag or read only
  changeView?: (date: string) => void; // Function to change view type
  shiftScheduleList: any[]; // Shift schedule list data
  reservationSettingMinMaxTime: any; // Reservation setting min-max time
}

const WeeklySchedulerComponent = (props: IProps) => {
  // Define WeeklySchedulerComponent functional component
  const {
    minuteInterval, // Minute interval
    minutes, // Minutes array
    hours, // Hour data
    hoursShown, // Hours to be displayed
    view, // View type
    getAllStaffOptions, // All staff options
    getBreaks, // Breaks data
    droppedEvents, // Dropped events
    minutesTd, // Minute intervals for table
    openTime, // Opening time
    selectedStaff, // Selected staff ID
    getStaffAppointmentsNonStaff, // Staff appointments data
    CELL_WIDTH, // Cell width
    DEFAULT_TOP_MARGIN, // Default top margin
    openReservationWindow, // Function to open reservation window
    currentDate, // Currently selected date
    handleStaffSelect, // Function to handle staff selection
    days, // Days data
    hourColumnWidthPx, // Hour column width in pixels
    minuteColumnWidthPx, // Minute column width in pixels
    tableRef, // Reference to the table element
    storeId, // Pass the store ID
    viewOnly, // View only flag
    changeView, // Function to change view type
    reservationSettingMinMaxTime, // Reservation setting min-max time
  } = props; // Destructure props

  const totalhours = hours.length; // Total number of hours

  const { reservationSchedulerStore } = useStore(); // Access store context

  return (
    // Render component
    <React.Fragment>
      {/* React fragment wrapper */}
      <div
        className="scheduler-table weekly-view" // Scheduler table class
        ref={tableRef} // Table ref
        style={{ width: "100%", minWidth: "900px" }} // Table style
      >
        {/* Table header with staff names and time slots */}
        <table className="table table-bordered">
          {" "}
          {/* Table element */}
          <thead>
            {" "}
            {/* Table head */}
            <tr>
              {" "}
              {/* Table row */}
              <th className="day-column" rowSpan={2}>
                {" "}
                {/* Staff column header */}
                {formatMessage("staff")} {/* Column for staff */}
              </th>
              <th className="day-column" rowSpan={2}>
                {" "}
                {/* Date column header */}
                {formatMessage("date")} {/* Column for date */}
              </th>
              <Hours
                hours={hours} // Passing hours data
                hoursShown={totalhours} // Passing hours to be displayed
                minuteInterval={minuteInterval} // Passing minute interval
                hourColumnWidthPx={hourColumnWidthPx} // Passing hour column width
              />
            </tr>
            <tr>
              {" "}
              {/* Table row for minutes */}
              <Minutes
                minutes={minutes} // Passing minutes data
                hoursShown={totalhours} // Passing hours to be displayed
                minuteColumnWidthPx={minuteColumnWidthPx} // Passing minute column width
              />
            </tr>
          </thead>
          <tbody>
            {" "}
            {/* Table body */}
            {days.map(
              (
                day: any,
                d: any // Map over days
              ) => (
                <tr key={d}>
                  {" "}
                  {/* Table row for each day */}
                  {view !== "daily" &&
                    d === 0 && ( // If not daily view and first row
                      <td className="day-column align-top " rowSpan={7}>
                        {" "}
                        {/* Staff column */}
                        <div className="flex flex-col items-center weekly_staff_column space-y-3 ">
                          {" "}
                          {/* Staff list container */}
                          <a
                            key={-1} // Key for all staff
                            onClick={() => handleStaffSelect(-1, "AllStaff")} // Handle selection for all staff
                            className={`d-block  my-2  ${
                              selectedStaff === -1 ? "selected" : "" // Highlight selected staff
                            }`}
                          >
                            <img
                              src={allStaffIcon} // Staff image
                              alt={formatMessage("all_staff")} // Staff name as alt text
                              className="staffImg" // Staff image class
                            />
                            <h5 className="text-muted text-center fw-normal">
                              <FormattedMessage id="all_staff" />{" "}
                              {/* All staff label */}
                            </h5>
                          </a>
                          {getAllStaffOptions?.map(
                            (
                              staff,
                              index // Map over staff options
                            ) => (
                              <a
                                key={staff.id} // Staff key
                                onClick={
                                  () =>
                                    handleStaffSelect(
                                      staff.id,
                                      staff?.data?.StaffName
                                    ) // Handle individual staff selection
                                }
                                className={`d-block my-2 ${
                                  selectedStaff === staff.id ? "selected" : "" // Highlight selected staff
                                }`}
                              >
                                <img
                                  src={staff.data?.Image ?? demoStoreIcon} // Staff image
                                  alt={staff.data?.StaffName} // Staff name as alt text
                                  className="staffImg" // Staff image class
                                />
                                <h5 className="text-muted text-center fw-normal">
                                  {staff.data?.StaffName} {/* Staff name */}
                                </h5>
                              </a>
                            )
                          )}
                        </div>
                      </td>
                    )}
                  <td className="day-column">{day}</td>{" "}
                  {/* Render day column */}
                  {/* Render the time slots for each hour and minute interval */}
                  {[...Array(totalhours)].map(
                    (
                      _,
                      h // Map over hours
                    ) =>
                      minutesTd.map((m, i) => {
                        // Map over minutes
                        const baseHour = moment(openTime, "HH:mm").hour(); // Calculate base hour
                        const hour = baseHour + h; // Calculate current hour
                        const timeLabel = formatTimeString(`${hour}${m}`); // Format time label
                        const params = {
                          date: moment(day.props["data-index"])?.format(
                            "YYYY-MM-DD" // Format date
                          ),
                          time: timeLabel, // Time label
                          staffId: Number(day.props["data-action"]), // Staff ID
                        };

                        let allMatchedEvents = getOverlappingEventsByWeeklyTime(
                          getStaffAppointmentsNonStaff, // Get overlapping events
                          params
                        );

                        let matchedEvents = allMatchedEvents; // Matched events

                        let matchedBreaks = getOverlappingBreaksByWeeklyTime(
                          getBreaks, // Get overlapping breaks
                          params
                        );

                        const tbrc = matchedEvents?.filter(
                            (item: any) => item.HasBreakConflict == true
                          )?.length;

                        matchedEvents = matchedEvents?.filter(
                          (item: any) => item.IsShow == true // Filter visible events
                        );
                        const totalRenderedCount = matchedEvents?.filter(
                          (item: any) => item.IsShow == true // Count visible events
                        )?.length;

                        const totalRenderedCountBreak = matchedBreaks?.filter(
                          (item: any) => item.IsShow == true // Count visible breaks
                        )?.length;
                        
                        let rows = 1; // Default rows count
                          if (totalRenderedCount + tbrc >= 2) {
                            rows = 2;
                          } else {
                            rows =
                              totalRenderedCount + tbrc == 0
                                ? 1
                                : totalRenderedCount + tbrc; // Calculate rows based on rendered count
                          }

                        let isOverlappingSameTime = false; // Flag for overlapping events
                        if (
                          matchedEvents?.length == 2 &&
                          matchedEvents[0]?.StartTime ==
                            matchedEvents[1]?.StartTime
                        ) {
                          isOverlappingSameTime = true; // Check for overlapping events
                        } else {
                          isOverlappingSameTime = false; // No overlapping events
                        }

                        let isOverlappingSameTimeBreak = false; // Flag for overlapping breaks
                        if (
                          matchedBreaks?.length == 2 &&
                          matchedBreaks[0]?.StartTime ==
                            matchedBreaks[1]?.StartTime
                        ) {
                          isOverlappingSameTimeBreak = true; // Check for overlapping breaks
                        } else {
                          isOverlappingSameTimeBreak = false; // No overlapping breaks
                        }

                        let shift = props.shiftScheduleList?.find(
                          (item: any) =>
                            item?.StaffId == day.props["data-action"]
                        );

                        if (matchedEvents?.length > 0) {
                          // If there are matched events
                          return (
                            <td
                              key={`WEEKLY-MATCHED-${day.props["data-index"]}-${timeLabel}-IN-${i}`} // Unique key for cell
                              className={`minute-cell position-relative 
                            
                            ${
                              isCurrentDateTimeGreater(
                                moment(day?.props["data-index"]).format(
                                  "YYYY-MM-DD"
                                ),
                                timeLabel
                              ) == true || viewOnly
                                ? `bg-gray`
                                : isDayClosedWeekly(
                                    props.getReservationSettingDetail,
                                    moment(day?.props["data-index"]).format(
                                      "YYYY-MM-DD"
                                    )
                                  ) == true
                                ? `bg-gray`
                                : props.shiftScheduleList?.some(
                                    (item) =>
                                      item.StaffId == day.props["data-action"]
                                  )
                                ? isTimeWithinShift(shift, timeLabel)
                                  ? "bg-inside-shift"
                                  : isTimeOuterThanShiftColourModeUsingMinMaxTimeEffortWeekly(
                                      props.reservationSettingMinMaxTime,
                                      timeLabel,
                                      moment(day?.props["data-index"]).format(
                                        "YYYY-MM-DD"
                                      )
                                    ) == true
                                  ? "bg-outside-shift"
                                  : "bg-inside-business-hours"
                                : `bg-shift-not-assigned`
                            }  
                                                         
                          
                          
                          `}
                              style={{
                                width: CELL_WIDTH, // Set cell width
                                height: rows * DEFAULT_TOP_MARGIN, // Set cell height
                              }}

                              data-label={`WEEKLY-MATCHED-${day.props["data-index"]}-${timeLabel}-IN-${i}`}
                            >
                            
                              {matchedEvents.map(
                                (matchedId: any, index: number) => { 
                                  var finalObject =
                                    getStaffAppointmentsNonStaff?.find(
                                      (item) => item?.Id == matchedId?.Id // Find matching event
                                    );

                                  let allOverlappingBreaks =
                                    finalObject?.OverlappedBreaks?.map(
                                      (breakItem: any) =>
                                        getBreaks?.find(
                                          (item) =>
                                            item?.BreakID == breakItem?.BreakID
                                        )
                                    );

                                  let totalColspan = Math.ceil(
                                    Number(finalObject?.TotalTime) /
                                      minuteInterval // Calculate colspan
                                  );
                                  let startEnd = Array.from(
                                    { length: totalColspan },
                                    (_, i) => i + 1 // Generate start and end range
                                  );
                                  let start = startEnd[0]; // Start time
                                  let end = startEnd[startEnd?.length - 1]; // End time
                                  const overlappingCount =
                                    matchedEvents?.length - 1; // Count overlapping events

                                  if (finalObject?.StartTime == timeLabel) {
                                    // If event starts at this time
                                    let trbc =
                                      finalObject?.OverlappedBreaks?.length;
                                    if (finalObject?.IsShow == true) {
                                      // If event is visible
                                      return (
                                        <React.Fragment
                                          key={`WEEKLY-RF-RES-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                        >
                                          {finalObject?.AppointmentStatus == // Check appointment status
                                          bookingStatusEnum.Complete ? (
                                            <CompletedEventComponent
                                              key={`WEEKLY-RES-COMP-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                              day={day} // Pass day data
                                              currentDate={moment(
                                                currentDate
                                              ).format("YYYY-MM-DD")} // Format current date
                                              dataObject={finalObject} // Pass event data
                                              timeLabel={timeLabel} // Pass time label
                                              start={start} // Pass start index
                                              end={end} // Pass end index
                                              overlappingCount={
                                                isOverlappingSameTime == true
                                                  ? index
                                                  : overlappingCount
                                              } // Pass overlapping count or Index
                                              allEvents={
                                                getStaffAppointmentsNonStaff
                                              } // Pass all events
                                              allBreaks={getBreaks} // Pass all breaks
                                              defaultMargin={DEFAULT_TOP_MARGIN} // Pass default margin
                                              events={allMatchedEvents} // Pass matched events
                                              cellWidth={CELL_WIDTH} // Pass cell width
                                              storeId={storeId} // Pass the store ID
                                              viewOnly={viewOnly} // Pass the view only flag or read only
                                              index={index} // Pass index
                                              breaksCount={trbc}
                                              allMatchedBreaks={
                                                allOverlappingBreaks
                                              } // Pass all breaks
                                              staffList={getAllStaffOptions} // Pass staff list
                                            />
                                          ) : finalObject?.AppointmentStatus ==
                                            bookingStatusEnum.InProcess ? (
                                            <React.Fragment>
                                              <ReceptionEventComponent
                                                key={`WEEKLY-RES-INP-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                                day={day} // Pass day data
                                                currentDate={moment(
                                                  currentDate
                                                ).format("YYYY-MM-DD")} // Format current date
                                                dataObject={finalObject} // Pass event data
                                                timeLabel={timeLabel} // Pass time label
                                                start={start} // Pass start index
                                                end={end} // Pass end index
                                                overlappingCount={
                                                  isOverlappingSameTime == true
                                                    ? index
                                                    : overlappingCount
                                                } // Pass overlapping count or Index
                                                allEvents={
                                                  getStaffAppointmentsNonStaff
                                                } // Pass all events
                                                allBreaks={getBreaks}
                                                defaultMargin={
                                                  DEFAULT_TOP_MARGIN
                                                } // Pass default margin
                                                events={allMatchedEvents} // Pass matched events
                                                cellWidth={CELL_WIDTH} // Pass cell width
                                                storeId={storeId} // Pass the store ID
                                                viewOnly={viewOnly} // Pass view only flag
                                                index={index} // Pass index
                                                breaksCount={trbc}
                                                allMatchedBreaks={
                                                  allOverlappingBreaks
                                                } // Pass all breaks
                                                staffList={getAllStaffOptions} // Pass staff list
                                              />
                                            </React.Fragment>
                                          ) : (
                                            <React.Fragment>
                                            <InProgressEvent
                                              key={`WEEKLY-RES-INP-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                              day={day} // Pass day data
                                              currentDate={moment(
                                                currentDate
                                              ).format("YYYY-MM-DD")} // Format current date
                                              dataObject={finalObject} // Pass event data
                                              timeLabel={timeLabel} // Pass time label
                                              start={start} // Pass start index
                                              end={end} // Pass end index
                                              overlappingCount={
                                                isOverlappingSameTime == true
                                                  ? index
                                                  : overlappingCount
                                              } // Pass overlapping count or Index
                                              allEvents={
                                                getStaffAppointmentsNonStaff
                                              } // Pass all events
                                              allBreaks={getBreaks} // Pass all breaks
                                              defaultMargin={DEFAULT_TOP_MARGIN} // Pass default margin
                                              events={allMatchedEvents} // Pass matched events
                                              cellWidth={CELL_WIDTH} // Pass cell width
                                              storeId={storeId} // Pass the store ID
                                              viewOnly={viewOnly} // Pass the view only flag or read only
                                              changeView={changeView} // Pass the change view function
                                              index={index} // Pass index
                                              breaksCount={trbc >= 0 ? trbc : matchedBreaks?.length}
                                              allMatchedBreaks={
                                                allOverlappingBreaks
                                              } // Pass all breaks
                                              staffList={getAllStaffOptions} // Pass staff list
                                            />
                                            </ React.Fragment>
                                          )}



                                          {index == 0 &&
                                            isOverlappingSameTime == true && ( // If overlapping at same time
                                              <EmptyCell
                                                key={`WEEKLY-RES-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                                staffId={
                                                  day.props["data-action"]
                                                } // Staff ID
                                                staffName={day.props["data-id"]} // Pass staff name
                                                timeLabel={timeLabel} // Pass time label
                                                disabled={
                                                  viewOnly
                                                    ? viewOnly
                                                    : isCurrentDateTimeGreater(
                                                        moment(
                                                          day?.props[
                                                            "data-index"
                                                          ]
                                                        ).format("YYYY-MM-DD"),
                                                        timeLabel
                                                      ) == true
                                                    ? false //true
                                                    : isDayClosedWeekly(
                                                        props.getReservationSettingDetail,
                                                        moment(
                                                          day?.props[
                                                            "data-index"
                                                          ]
                                                        ).format("YYYY-MM-DD")
                                                      ) == true
                                                    ? true
                                                    : false
                                                } // Set disabled to false
                                                day={day} // Pass day data
                                                staffList={getAllStaffOptions} // Pass staff list
                                                openWindow={
                                                  openReservationWindow
                                                } // Pass open window function
                                                storeId={storeId} // Pass the store ID
                                                type="overlap1"
                                              />
                                            )}

                                          {index == 0 &&
                                            isOverlappingSameTime == false &&
                                            matchedEvents?.length == 1 && ( // If only one event and not overlapping
                                              <EmptyCell
                                                key={`WEEKLY-RES-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                                staffId={
                                                  day.props["data-action"]
                                                } // Staff ID
                                                staffName={day.props["data-id"]} // Pass staff name
                                                timeLabel={timeLabel} // Pass time label
                                                disabled={
                                                  viewOnly
                                                    ? viewOnly
                                                    : isCurrentDateTimeGreater(
                                                        moment(
                                                          day?.props[
                                                            "data-index"
                                                          ]
                                                        ).format("YYYY-MM-DD"),
                                                        timeLabel
                                                      ) == true
                                                    ? false //true
                                                    : isDayClosedWeekly(
                                                        props.getReservationSettingDetail,
                                                        moment(
                                                          day?.props[
                                                            "data-index"
                                                          ]
                                                        ).format("YYYY-MM-DD")
                                                      ) == true
                                                    ? true
                                                    : false
                                                } // Set disabled to false
                                                day={day} // Pass day data
                                                staffList={getAllStaffOptions} // Pass staff list
                                                openWindow={
                                                  openReservationWindow
                                                } // Pass open window function
                                                storeId={storeId} // Pass the store ID
                                                type="overlap2"
                                              />
                                            )}
                                        </React.Fragment>
                                      );
                                    } else {
                                      // If event is not visible
                                      return (
                                        <React.Fragment
                                          key={`WEEKLY-RF-EMP-RES-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                        >
                                          <EmptyCell
                                            key={`RES-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                            staffId={day.props["data-action"]} // Staff ID
                                            staffName={day.props["data-id"]} // Staff name
                                            timeLabel={timeLabel} // Time label
                                            disabled={
                                              viewOnly
                                                ? viewOnly
                                                : isCurrentDateTimeGreater(
                                                    moment(
                                                      day?.props["data-index"]
                                                    ).format("YYYY-MM-DD"),
                                                    timeLabel
                                                  ) == true
                                                ? false  //true
                                                : isDayClosedWeekly(
                                                    props.getReservationSettingDetail,
                                                    moment(
                                                      day?.props["data-index"]
                                                    ).format("YYYY-MM-DD")
                                                  ) == true
                                                ? true
                                                : false
                                            } // Set disabled to false
                                            day={day} // Day data
                                            staffList={getAllStaffOptions} // Staff list
                                            openWindow={openReservationWindow} // Open reservation window
                                            storeId={storeId} // Pass the store ID
                                            type="overlap3"
                                          />
                                        </React.Fragment>
                                      );
                                    }
                                  } else {
                                    // If event does not start at this time
                                    return (
                                      <React.Fragment
                                        key={`WEEKLY-RF-EMP-RES-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                      >
                                        {index == 0 && (
                                        <EmptyCell
                                          key={`WEEKLY-RES-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                          staffId={day.props["data-action"]} // Staff ID
                                          staffName={day.props["data-id"]} // Staff name
                                          timeLabel={timeLabel} // Time label
                                          day={day} // Day data
                                          disabled={
                                            viewOnly
                                              ? viewOnly
                                              : isCurrentDateTimeGreater(
                                                  moment(
                                                    day?.props["data-index"]
                                                  ).format("YYYY-MM-DD"),
                                                  timeLabel
                                                ) == true
                                              ? false //true
                                              : isDayClosedWeekly(
                                                  props.getReservationSettingDetail,
                                                  moment(
                                                    day?.props["data-index"]
                                                  ).format("YYYY-MM-DD")
                                                ) == true
                                              ? true
                                              : false
                                          } // Set disabled to false
                                          staffList={getAllStaffOptions} // Staff list
                                          openWindow={openReservationWindow} // Open reservation window
                                          storeId={storeId} // Pass the store ID
                                          type="overlap4"
                                        />
                                        )}
                                      </React.Fragment>
                                    );
                                  }
                                }
                              )}

                              {/* Render overlapping breaks if less than 2 events */}
                              {totalRenderedCount < 2 &&
                                matchedEvents?.map(
                                  (event: any, index: number) => {

                                    // Number of overlapped breaks for this event
                                    let trbc = event?.OverlappedBreaks?.length;
                                    // Map over each overlapped break
                                    return event?.OverlappedBreaks?.map(
                                      (breakItem: any) => {
                                        // Find the break object
                                        var finalObject = getBreaks?.find(
                                          (item) =>
                                            item?.BreakID == breakItem?.BreakID
                                        );
                                        
                                        let allOverlappingBreaks =
                                      finalObject?.OverlappedBreaks?.map(
                                        (breakItem: any) =>
                                          getBreaks?.find(
                                            (item) =>
                                              item?.BreakID ==
                                              breakItem?.BreakID
                                          )
                                      );

                                        
                                        // Calculate colspan for the break
                                        let totalColspan = Math.ceil(
                                          Number(finalObject?.TotalTime) /
                                            minuteInterval
                                        );
                                        // Create array for start and end
                                        let startEnd = Array.from(
                                          { length: totalColspan },
                                          (_, i) => i + 1
                                        );
                                        // Start and end index for the break
                                        let start = startEnd[0];
                                        let end =
                                          startEnd[startEnd?.length - 1];
                                        // Overlapping count for breaks
                                        const overlappingCount = 1;
                                        

                                        
                                        // If break starts at this time and is visible
                                        if (
                                          finalObject?.StartTime == timeLabel
                                        ) {
                                          if (breakItem?.IsShow == true) {
                                            return (
                                              <React.Fragment
                                                key={`WEEKLY-RF-BR-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                              >
                                                <BreakEventComponent
                                                  day={day}
                                                  currentDate={moment(
                                                    currentDate
                                                  ).format("YYYY-MM-DD")}
                                                  dataObject={finalObject}
                                                  timeLabel={timeLabel}
                                                  start={start}
                                                  end={end}
                                                  overlappingCount={
                                                    overlappingCount
                                                  }
                                                  staffList={getAllStaffOptions}
                                                  defaultMargin={
                                                    DEFAULT_TOP_MARGIN
                                                  }
                                                  allMatchedEvents={
                                                    matchedBreaks
                                                  }
                                                  allMatchedBreaks={allOverlappingBreaks}
                                                  cellWidth={CELL_WIDTH}
                                                  storeId={storeId}
                                                  viewOnly={viewOnly}
                                                  // breaksCount is number of breaks minus 1
                                                  breaksCount={trbc}
                                                  // Show "All More Events" popup if total events and breaks > 2
                                                  showAllMoreEventsPopup={matchedEvents.length > 0 && event.OverlappedBreaks.length > 1 ? true : false}
                                                />
                                              </React.Fragment>
                                            );
                                          }
                                        }
                                      }
                                    );
                                  }
                                )}
                            </td>
                          );
                        } else if (matchedBreaks?.length > 0) {
                          // console.log("RENDERING BREAKS :", matchedBreaks, " AT TIME :", timeLabel, " FOR STAFF ID :", day.props["data-action"]);
                          // If there are matched breaks
                          return (
                            <td
                              key={`WEEKLY-BR-${day.props["data-index"]}-${timeLabel}-IN-${i}`} // Unique key for break cell
                              className={`minute-cell position-relative 
                            
                            // ${
                            //   isCurrentDateTimeGreater(
                            //     moment(day?.props["data-index"]).format(
                            //       "YYYY-MM-DD"
                            //     ),
                            //     timeLabel
                            //   ) == true ||
                              
                              viewOnly
                                ? `bg-gray`
                                : isDayClosedWeekly(
                                    props.getReservationSettingDetail,
                                    moment(day?.props["data-index"]).format(
                                      "YYYY-MM-DD"
                                    )
                                  ) == true
                                ? `bg-gray`
                                : props.shiftScheduleList?.some(
                                    (item) =>
                                      item.StaffId == day.props["data-action"]
                                  )
                                ? isTimeWithinShift(shift, timeLabel)
                                  ? "bg-inside-shift"
                                  : isTimeOuterThanShiftColourModeUsingMinMaxTimeEffortWeekly(
                                      props.reservationSettingMinMaxTime,
                                      timeLabel,
                                      moment(day?.props["data-index"]).format(
                                        "YYYY-MM-DD"
                                      )
                                    ) == true
                                  ? "bg-outside-shift"
                                  : "bg-inside-business-hours"
                                : `bg-shift-not-assigned`
                            }                 

                          `}
                              style={{
                                width: CELL_WIDTH, // Set cell width
                                height:
                                  totalRenderedCountBreak * DEFAULT_TOP_MARGIN, // Set cell height
                              }}
                            >
                              {matchedBreaks?.map(
                                (matchedId: any, index: number) => {
                                  // Map over matched breaks
                                  var finalObject = getBreaks?.find(
                                    (item) =>
                                      item?.BreakID == matchedId?.BreakID // Find matching break
                                  );

                                  let allOverlappingBreaks =
                                      finalObject?.OverlappedBreaks?.map(
                                        (breakItem: any) =>
                                          getBreaks?.find(
                                            (item) =>
                                              item?.BreakID ==
                                              breakItem?.BreakID
                                          )
                                      );

                                  let totalColspan = Math.ceil(
                                    Number(finalObject?.TotalTime) /
                                      minuteInterval // Calculate colspan
                                  );
                                  let startEnd = Array.from(
                                    { length: totalColspan },
                                    (_, i) => i + 1 // Generate start and end range
                                  );
                                  let start = startEnd[0]; // Start time
                                  let end = startEnd[startEnd?.length - 1]; // End time
                                  
                                  let trbc = finalObject?.OverlappedBreaks?.length > 2 ? finalObject?.OverlappedBreaks?.length - 2 : finalObject?.OverlappedBreaks?.length;

                                  // const overlappingCount = matchedBreaks?.length - 1;
                                  const overlappingCount = matchedBreaks?.length -1 >= 2 && index == 0 ? 0 : matchedBreaks?.length - 1 >=2 && index == 1 ? 1 : matchedBreaks?.length -1; // Count overlapping breaks

                                  if (finalObject?.StartTime == timeLabel) {
                                    // If break starts at this time
                                    if (
                                      finalObject?.IsShow == true // Check visibility and staff selection
                                    ) {
                                      return (
                                        <React.Fragment
                                          key={`WEEKLY-RF-BR-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                        >
                                          <BreakEventComponent
                                            day={day} // Day data
                                            currentDate={moment(
                                              currentDate
                                            ).format(
                                              "YYYY-MM-DD" // Format current date
                                            )}
                                            dataObject={finalObject} // Break data
                                            timeLabel={timeLabel} // Time label
                                            start={start} // Start time
                                            end={end} // End time
                                            overlappingCount={overlappingCount} // Overlapping count
                                            staffList={getAllStaffOptions} // Staff list
                                            defaultMargin={DEFAULT_TOP_MARGIN} // Default margin
                                            allMatchedEvents={matchedBreaks} // All matched breaks
                                            allMatchedBreaks={allOverlappingBreaks}
                                            cellWidth={CELL_WIDTH} // Cell width
                                            storeId={storeId} // Pass the store ID
                                            viewOnly={viewOnly} // Pass the view only flag or read 
                                            index={index} // Pass index
                                            breaksCount={trbc}
                                            showAllMoreEventsPopup={false}
                                          />
                                          {/* {index == 0 && (
                                            <React.Fragment
                                              key={`RF-BR-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                            >
                                              <EmptyCell
                                                key={`BR-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                                staffId={
                                                  day.props["data-action"]
                                                } // Staff ID
                                                staffName={
                                                  day.props["data-id"]
                                                } // Pass staff name
                                                timeLabel={timeLabel} // Pass time label
                                                disabled={
                                                  viewOnly
                                                    ? viewOnly
                                                    : isCurrentDateTimeGreater(
                                                        moment(
                                                          day?.props[
                                                            "data-index"
                                                          ]
                                                        ).format(
                                                          "YYYY-MM-DD"
                                                        ),
                                                        timeLabel
                                                      ) == true
                                                    ? true
                                                    : false
                                                } // Set disabled to false
                                                day={day} // Pass day data
                                                staffList={
                                                  getAllStaffOptions
                                                } // Pass staff list
                                                openWindow={
                                                  openReservationWindow
                                                } // Pass open window function
                                                storeId={storeId} // Pass the store ID
                                                type="o1"
                                              />
                                            </React.Fragment>
                                          )} */}
                                        </React.Fragment>
                                      );
                                    } else {
                                      // If break is not visible
                                      return (
                                        <React.Fragment
                                          key={`WEEKLY-RF-BR-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                        >
                                          <EmptyCell
                                            key={`WEEKLY-BR-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                            staffId={day.props["data-action"]} // Staff ID
                                            staffName={day.props["data-id"]} // Staff name
                                            timeLabel={timeLabel} // Time label
                                            disabled={
                                              viewOnly
                                                ? viewOnly
                                                : isCurrentDateTimeGreater(
                                                    moment(
                                                      day?.props["data-index"]
                                                    ).format("YYYY-MM-DD"),
                                                    timeLabel
                                                  ) == true
                                                ? true
                                                : isDayClosedWeekly(
                                                    props.getReservationSettingDetail,
                                                    moment(
                                                      day?.props["data-index"]
                                                    ).format("YYYY-MM-DD")
                                                  ) == true
                                                ? true
                                                : false
                                            } // Set disabled to false
                                            day={day} // Day data
                                            staffList={getAllStaffOptions} // Staff list
                                            openWindow={openReservationWindow} // Open reservation window
                                            storeId={storeId} // Pass the store ID
                                            // type="o2"
                                          />
                                        </React.Fragment>
                                      );
                                    }
                                  } else {
                                    // If break does not start at this time
                                    return (
                                      <React.Fragment
                                        key={`WEEKLY-RF-BR-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                      >
                                        {index == 0 && (

                                        <EmptyCell
                                          key={`WEEKLY-BR-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key
                                          staffId={day.props["data-action"]} // Staff ID
                                          staffName={day.props["data-id"]} // Staff name
                                          timeLabel={timeLabel} // Time label
                                          disabled={
                                            viewOnly
                                              ? viewOnly
                                              : isCurrentDateTimeGreater(
                                                  moment(
                                                    day?.props["data-index"]
                                                  ).format("YYYY-MM-DD"),
                                                  timeLabel
                                                ) == true
                                              ? false //true
                                              : isDayClosedWeekly(
                                                  props.getReservationSettingDetail,
                                                  moment(
                                                    day?.props["data-index"]
                                                  ).format("YYYY-MM-DD")
                                                ) == true
                                              ? true
                                              : false
                                          } // Set disabled to false
                                          day={day} // Day data
                                          staffList={getAllStaffOptions} // Staff list
                                          openWindow={openReservationWindow} // Open reservation window
                                          storeId={storeId} // Pass the store ID
                                          // type="o3"
                                        />
                                        )}
                                      </React.Fragment>
                                    );
                                  }
                                }
                              )}
                            </td>
                          );
                        } else {
                          // If no events or breaks
                          return (
                            <td
                              key={`${day}-${timeLabel}`} // Unique key for empty cell
                              className={`minute-cell position-relative 

                            // ${
                            //   isCurrentDateTimeGreater(
                            //     moment(day?.props["data-index"]).format(
                            //       "YYYY-MM-DD"
                            //     ),
                            //     timeLabel
                            //   ) == true || 
                              
                              viewOnly
                                ? `bg-gray`
                                : isDayClosedWeekly(
                                    props.getReservationSettingDetail,
                                    moment(day?.props["data-index"]).format(
                                      "YYYY-MM-DD"
                                    )
                                  ) == true
                                ? `bg-gray`
                                : props.shiftScheduleList?.some(
                                    (item) =>
                                      item.StaffId == day.props["data-action"]
                                  )
                                ? isTimeWithinShift(shift, timeLabel)
                                  ? "bg-inside-shift"
                                  : isTimeOuterThanShiftColourModeUsingMinMaxTimeEffortWeekly(
                                      props.reservationSettingMinMaxTime,
                                      timeLabel,
                                      moment(day?.props["data-index"]).format(
                                        "YYYY-MM-DD"
                                      )
                                    ) == true
                                  ? "bg-outside-shift"
                                  : "bg-inside-business-hours"
                                : `bg-shift-not-assigned`
                            }  
                                                         
                          
                          `}
                              style={{
                                display: "table-cell", // Set display style
                                width: CELL_WIDTH, // Set cell width
                              }}
                            >
                              {/* Draggable event component */}
                              <EmptyCell
                                key={`WEEKLY-EMPTY-${day.props["data-index"]}-${timeLabel}}`} // Unique key
                                staffId={day.props["data-action"]} // Staff ID
                                staffName={day.props["data-id"]} // Staff name
                                timeLabel={timeLabel} // Time label
                                day={day} // Day data
                                disabled={
                                  viewOnly
                                    ? viewOnly
                                    : isCurrentDateTimeGreater(
                                        moment(day?.props["data-index"]).format(
                                          "YYYY-MM-DD"
                                        ),
                                        timeLabel
                                      ) == true
                                    ? false //true
                                    : isDayClosedWeekly(
                                        props.getReservationSettingDetail,
                                        moment(day?.props["data-index"]).format(
                                          "YYYY-MM-DD"
                                        )
                                      ) == true
                                    ? true
                                    : false
                                } // Set disabled to false
                                staffList={getAllStaffOptions} // Staff list
                                openWindow={openReservationWindow} // Open reservation window
                                storeId={storeId} // Pass the store ID
                              />
                            </td>
                          );
                        }
                      })
                  )}
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
      {/* {JSON.stringify(getBreaks)} */}
    </React.Fragment>
  );
};

export default observer(WeeklySchedulerComponent); // Exporting the component wrapped with observer
